import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import styled from 'styled-components';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { Button as AntdButton, Dropdown as AntdDropdown } from 'antd';
import {
	AiOutlineDelete,
	AiOutlineEdit,
	AiOutlineMore,
	AiOutlineTags,
	AiOutlineHistory,
} from 'react-icons/ai';

import { useAuth } from '@contexts/User.context';
import { humanFileSize } from '@utils/files';
import { buildProjectUrl, projectHomeUrl } from '@utils/projectPath';
import {
	DEFAULT_PROJECT_VIEW_MODE,
	IMAGE_MODE,
	MODEL_VIEW_MODE_ENABLED,
	PROJECT_VIEW_MODE,
} from '@utils/constants';
import ProjectTags from './projectTags/ProjectTagsWrapper';
import ProjectThumbnail from './ProjectThumbnail';
import ProjectTagsModal from './projectTags/projectTagsModal';
import ArchiveProjectDialog from '@components/archiveProject/ArchiveProject';
import ProjectProgress from './ProjectProgress';

const StyledProjectCard = styled(Card)`
	color: ${p => p.theme.colors.textColor};
	background-color: ${p =>
		p.demo === 'true'
			? p.theme.colors.backgroundAccent
			: p.error === 'true'
				? 'var(--bs-danger-bg-subtle)'
				: p.theme.colors.modal};
	border: none;
	margin-bottom: calc(var(--bs-gutter-x));
	min-height: calc(100% - var(--bs-gutter-x));

	.card-footer {
		background: none;
	}
`;

const StyledActionsWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	padding-bottom: var(--bs-card-spacer-y);

	.action-btn {
		font-weight: 400;
		background: ${props => props.theme.colors.border};

		&:not(:hover) {
			background-color: ${p => p.theme.colors.border};
		}

		&:hover {
			background: #f8f9fa !important;
			color: black !important;
		}

		&:focus-visible {
			box-shadow: rgba(248, 249, 250, 0.5) 0px 0px 0px 4px;
			color: #f8f9fa;
			background: ${props => props.theme.colors.border};
		}
	}
`;

const ProjectCard = ({ project, refetchProjects }) => {
	const { tierTrial, tierPro } = useAuth();
	const navigate = useNavigate();

	const [showTagsModal, setShowTagsModal] = useState(false);
	const [projectToBeArchived, setProjectToBeArchived] = useState(null);

	const {
		uuid: projectID,
		title,
		description,
		created_at: createdAt,
		tags,
		file_storage: fileStorage,
		orthophoto_progress,
		demo_project: isDemo,
		center_coordinate: center,
		image_mode: imageMode,
	} = project;

	const isVideoProject = imageMode === IMAGE_MODE.VIDEO;
	const projectPath = projectHomeUrl(projectID, imageMode);

	const error = !!orthophoto_progress?.error;
	const creating =
		!error && !isVideoProject && (!center || (center[0] === -1 && center[1] === -1));

	const handleDropdownSelect = eventKey => {
		switch (eventKey) {
			case 'edit':
				navigate(projectHomeUrl(projectID, imageMode, DEFAULT_PROJECT_VIEW_MODE, 'edit'));
				break;
			case 'view-model-mode':
				navigate(buildProjectUrl(projectID, PROJECT_VIEW_MODE.MODEL));
				break;
			case 'tags':
				setShowTagsModal(true);
				break;
			case 'delete':
				setProjectToBeArchived({ id: projectID, title: title });
				break;
			default:
				console.log('Unknown eventKey: ', eventKey);
		}
	};

	return (
		<StyledProjectCard error={error.toString()} demo={isDemo.toString()}>
			<ProjectTags uuid={projectID} tags={tags} />

			<ProjectThumbnail uuid={projectID} projectPath={projectPath} imageMode={imageMode} />
			<ProjectProgress project={project} />

			<Card.Body>
				{error && (
					<p className="alert alert-danger small p-2">Project processing has failed</p>
				)}

				<Card.Title>{title ? title : 'Untitled project'}</Card.Title>
				<Card.Subtitle className="mb-2 text-muted fst-italic">
					{new Date(createdAt).toLocaleDateString('nb-NO', {
						day: 'numeric',
						month: 'short',
						year: 'numeric',
					})}
				</Card.Subtitle>

				<Card.Text>{description}</Card.Text>

				{tierPro && !isDemo && (
					<p className="small text-muted mb-0">{humanFileSize(fileStorage)} storage</p>
				)}
			</Card.Body>

			<Card.Footer className="border-0 background-none">
				<StyledActionsWrapper>
					<Button
						as={Link}
						to={projectPath}
						variant="dark"
						disabled={creating}
						className={creating && 'disabled'}>
						View Project
					</Button>

					{!isDemo && (
						<CardDropdown
							projectID={projectID}
							handleDropdownSelect={handleDropdownSelect}
							tierTrial={tierTrial}
							isDemo={isDemo}
							isVideoProject={isVideoProject}
						/>
					)}
				</StyledActionsWrapper>
			</Card.Footer>

			{showTagsModal && (
				<ProjectTagsModal
					show={showTagsModal}
					setShow={setShowTagsModal}
					projectId={projectID}
					projectTitle={title}
					existingTags={tags}
					callBack={refetchProjects}
				/>
			)}

			{projectToBeArchived && (
				<ArchiveProjectDialog
					project={projectToBeArchived}
					callBack={() => {
						setProjectToBeArchived(null);
						refetchProjects();
					}}
				/>
			)}
		</StyledProjectCard>
	);
};

export default ProjectCard;

const ActionToggleButton = styled(AntdButton)`
	&&& {
		width: 36px;
		height: 36px;
		display: flex;
		justify-content: center;
		align-items: center;
		background-color: ${p => p.theme.colors.border};
		font-size: 1.5rem;
	}
`;

const CardDropdown = ({ projectID, handleDropdownSelect, tierTrial, isVideoProject }) => {
	const items = [
		// video projects have no edit page yet (/video-projects/:uuid/edit is not a route)
		...(isVideoProject
			? []
			: [{ key: 'edit', label: 'Edit', icon: <AiOutlineEdit aria-hidden="true" /> }]),
		{ key: 'tags', label: 'Edit tags', icon: <AiOutlineTags aria-hidden="true" /> },
		// video projects have one page, no model-based view
		...(MODEL_VIEW_MODE_ENABLED && !isVideoProject
			? [
					{
						key: 'view-model-mode',
						label: 'View in model-based mode',
						icon: <AiOutlineHistory aria-hidden="true" />,
					},
				]
			: []),
		{ type: 'divider' },
		{
			key: 'delete',
			label: 'Delete',
			icon: <AiOutlineDelete aria-hidden="true" />,
			disabled: tierTrial,
		},
	];

	return (
		<AntdDropdown
			menu={{ items, onClick: ({ key }) => handleDropdownSelect(key) }}
			trigger={['click']}
			placement="bottomRight">
			<ActionToggleButton
				shape="circle"
				type="text"
				icon={<AiOutlineMore aria-hidden="true" />}
				aria-label={`Toggle actions for project ${projectID}`}
			/>
		</AntdDropdown>
	);
};
